import { ClassData, JackRabbitClassesDataResponse } from "../dtos/classes.type";
import { ALLOWED_FILTER_KEYS, URL_PARAMETERS } from "../utils/const.util";
import {
  filterByAge,
  filterByDays,
  filterByFullClasses,
  filterByGender,
  filterByKeyAndValue,
  filterByLocation,
  filterByMultipleValues,
  filterBySession,
} from "../utils/filters.util";
import { getAllParamsFromURL } from "../utils/url.util";
import { DEFAULT_CATEGORY } from "./default-category.service";
import { trackingService } from "./tracking.service";

/**
 * Re-map any Jackrabbit-style URL keys (e.g. `Cat2=Rattlers` from a
 * registration link) onto the wizard's canonical filter keys. Mutates the
 * given filters object in place.
 *
 * - `Cat<N>` (Jackrabbit) → `classtype` (wizard) when N matches the site's
 *   configured DEFAULT_CATEGORY (e.g. "category2" → "Cat2").
 * - `category` (legacy alias) → `classtype`.
 */
function migrateJackrabbitFilters(filters: Record<string, any>): void {
  if (filters["category"] && !filters[URL_PARAMETERS.CATEGORY]) {
    filters[URL_PARAMETERS.CATEGORY] = filters["category"];
    delete filters["category"];
  }
  const catNumber = DEFAULT_CATEGORY.replace("category", ""); // "1" | "2" | "3"
  const jackrabbitKey = `Cat${catNumber}`;
  if (filters[jackrabbitKey] && !filters[URL_PARAMETERS.CATEGORY]) {
    filters[URL_PARAMETERS.CATEGORY] = filters[jackrabbitKey];
  }
  // Always strip the Jackrabbit-style keys so they don't leak through to
  // the filter loop (and so the URL stays clean after sanitization).
  delete filters["Cat1"];
  delete filters["Cat2"];
  delete filters["Cat3"];
}

/**
 * A singleton that stores the list of data fetched from the server.
 *
 * Provides simple filtering options for the data;
 *
 */
class RYBConnectClasses {
  public static instance: RYBConnectClasses;
  private isLoading: boolean = false;
  private data: null | ClassData[] = null;
  private loadPromise: null | Promise<ClassData[]> = null;
  private CATEGORY_KEY = ryb_connect_classes_data.filters.wizard_category_key;
  private CATEGORY_VALUE =
    ryb_connect_classes_data.filters.wizard_category_value;
  private IS_FILTERED_LIST = true;

  constructor() {
    if (RYBConnectClasses.instance) {
      return RYBConnectClasses.instance;
    }
    trackingService.init();
    this.IS_FILTERED_LIST =
      Boolean(ryb_connect_classes_data.filters.wizard_category_key) &&
      ryb_connect_classes_data.filters.wizard_category_key !== "null";
    RYBConnectClasses.instance = this;
  }

  /**
   * Get's unfiltered data from the API or from the instance
   * @returns {data}
   */
  async getData() {
    if (this.data) {
      return this.data;
    }

    if (this.isLoading) {
      return this.loadPromise;
    }

    this.isLoading = true;
    this.loadPromise = new Promise(async (resolve, reject) => {
      try {
        // @note :
        // this is not really pretty - think about of better way to optimize
        // dynamically the combination between category and category value
        const hasCategoryOne =
          this.CATEGORY_VALUE &&
          this.CATEGORY_VALUE.toLocaleLowerCase() !== "all" &&
          this.CATEGORY_VALUE.toLocaleLowerCase() !== "null";

        const apiUrl = hasCategoryOne
          ? `${ryb_connect_connection_details.url}&${this.CATEGORY_KEY}=${this.CATEGORY_VALUE}`
          : ryb_connect_connection_details.url;
        const response = await fetch(apiUrl, {
          method: "GET",
        });

        const data: JackRabbitClassesDataResponse = await response.json();
        if (data.success) {
          this.data =
            this.IS_FILTERED_LIST &&
            this.CATEGORY_VALUE !== "all" &&
            this.CATEGORY_VALUE !== "null"
              ? data.rows.filter(
                  (item) => item.category1 === this.CATEGORY_VALUE
                )
              : data.rows;

          this.data = [...this.data].sort((a, b) => {
            const timeA = new Date(`1970-01-01T${a.start_time}:00`);
            const timeB = new Date(`1970-01-01T${b.start_time}:00`);
            return timeA.getTime() - timeB.getTime();
          });
          this.isLoading = false;
          resolve(this.data);
        } else {
          trackingService.captureException(
            new Error(
              `${window.location.host} | Jackrabbit respond with error in ${window.location.pathname}`
            ),
            {
              requestedUrl: ryb_connect_connection_details.url,
              devMessage: "Jackrabbit returned error",
              response: data,
            }
          );
          this.isLoading = false;
          reject("Error: " + data);
        }
      } catch (error) {
        trackingService.captureException(
          new Error(
            `${window.location.host} | Exception - Jackrabbit respond with error in ${window.location.pathname}`
          ),
          {
            requestedUrl: ryb_connect_connection_details.url,
            devMessage: "Jackrabbit returned error",
            error: error,
          }
        );
        this.isLoading = false;
        reject("Error: Unable to fetch class openings.");
      }
    });

    return this.loadPromise;
  }

  /**
   * Get's filtered by parameters data
   *
   * @note This needs refactoring in order to make it more versatile. Currently it supports only our initial version.
   *
   * @returns {data}
   */
  async getFilteredDataByURLParams(): Promise<ClassData[]> {
    const filters = getAllParamsFromURL();
    if (!filters) {
      return this.data || [];
    }
    // Migrate legacy / Jackrabbit-style keys onto canonical wizard keys.
    migrateJackrabbitFilters(filters);
    let data = structuredClone(this.data) || [];

    // Delete not needed filters:
    if (filters[URL_PARAMETERS.STEP]) {
      delete filters[URL_PARAMETERS.STEP];
      delete filters[URL_PARAMETERS.USER];
      delete filters[URL_PARAMETERS.CONTACT];
    }

    // Handle session filter separately (not in generic loop).
    // Only apply in finder context (no wizard step active).
    const isWizardStep = !!filters[URL_PARAMETERS.STEP];
    const sessionTargetName = !isWizardStep
      ? (filters[URL_PARAMETERS.SESSION] || "")
      : "";
    delete filters[URL_PARAMETERS.SESSION];

    // Apply only known filter keys. Anything outside ALLOWED_FILTER_KEYS
    // (e.g. legacy Jackrabbit `Cat2=...` from registration links, `utm_*`
    // tracking params, etc.) is silently ignored — preventing the generic
    // by-key reducer from filtering classes against fields that don't exist.
    for (const [key, value] of Object.entries(filters)) {
      if (!ALLOWED_FILTER_KEYS.has(key)) continue;

      switch (key) {
        case URL_PARAMETERS.AGE:
          data = filterByAge(data);
          break;
        case URL_PARAMETERS.FULL_CLASSES:
          data = filterByFullClasses(data);
          break;
        case URL_PARAMETERS.CATEGORY:
          data = filterByMultipleValues(data, value);
          break;
        case URL_PARAMETERS.LOCATION:
          // Only apply the URL location filter when the wizard's location
          // step (step 2) isn't configured — otherwise step 2 owns location
          // selection and applies its own logic.
          if (!ryb_connect_classes_data.wizard.steps["2"]?.length) {
            data = filterByLocation(data);
          }
          break;
        default:
          // Known filter (gender, days) without a dedicated handler: filter
          // by direct field-name match on the class data.
          data = filterByKeyAndValue(data, key, value);
      }
    }

    if (sessionTargetName) {
      data = filterBySession(data, sessionTargetName);
    }

    return data;
  }

  /**
   * used for finder because of odd product requirements
   */
  async getFilteredDataByAgeAndGenderURLParams(): Promise<ClassData[]> {
    const filters = getAllParamsFromURL();
    if (!filters) {
      return this.data || [];
    }
    let data = structuredClone(this.data) || [];
    if (filters[URL_PARAMETERS.GENDER]) {
      data = filterByGender(data);
    }

    if (filters[URL_PARAMETERS.AGE]) {
      data = filterByAge(data);
    }

    if (filters[URL_PARAMETERS.FULL_CLASSES]) {
      data = filterByFullClasses(data);
    }

    if (filters[URL_PARAMETERS.DAYS]) {
      data = filterByDays(data, filters[URL_PARAMETERS.DAYS]);
    }

    if (filters[URL_PARAMETERS.SESSION]) {
      data = filterBySession(data, filters[URL_PARAMETERS.SESSION]);
    }

    return data;
  }
}

export default RYBConnectClasses;
